import { useEffect, useRef } from "react";
import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";

/**
 * LiveEffectsCanvas — real-time person segmentation + background replacement + beauty + LUT filters.
 *
 * Renders a canvas that composites the video with a new background and applies CSS filter chain.
 * Uses MediaPipe SelfieSegmentation (WebGL / WASM) — runs 20-30 FPS on mid-range Android.
 *
 * Props:
 *   sourceVideoRef  — the raw <video> element (already streaming from getUserMedia)
 *   background      — { type: "none"|"blur"|"image"|"color", value: string }
 *   filterCss       — combined CSS filter string (from Beauty + LUT)
 *   className       — style passthrough for the canvas
 *   fps             — target FPS (default 24, lower on slower devices)
 *   onReady         — optional callback invoked once when segmentation produces its first frame
 */
export default function LiveEffectsCanvas({ sourceVideoRef, background, filterCss, className, fps = 24, onReady }) {
  const canvasRef = useRef(null);
  const segRef = useRef(null);
  const bgImgRef = useRef(null);
  const rafRef = useRef(null);
  const lastRunRef = useRef(0);
  const activeBgRef = useRef(background);
  const firedReadyRef = useRef(false);

  // Keep the latest background in a ref so onResults sees fresh values without re-init
  useEffect(() => { activeBgRef.current = background; }, [background]);

  // Preload background image
  useEffect(() => {
    if (background?.type === "image" && background.value) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => { bgImgRef.current = img; };
      img.src = background.value;
    } else {
      bgImgRef.current = null;
    }
  }, [background?.type, background?.value]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const video = sourceVideoRef?.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext("2d");

    let cancelled = false;
    const sizeCanvas = () => {
      const w = video.videoWidth || 720;
      const h = video.videoHeight || 1280;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
    };

    // Initialise MediaPipe once
    const seg = new SelfieSegmentation({
      locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${f}`,
    });
    seg.setOptions({ modelSelection: 1, selfieMode: true });
    seg.onResults((results) => {
      if (cancelled) return;
      // First successful frame → notify parent that lens is ready (dismisses "Warming up..." overlay)
      if (!firedReadyRef.current) {
        firedReadyRef.current = true;
        try { onReady && onReady(); } catch { /* noop */ }
      }
      sizeCanvas();
      const w = canvas.width, h = canvas.height;
      const bg = activeBgRef.current || { type: "none" };

      // Draw new background first
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      if (bg.type === "blur") {
        // Draw the video blurred as background
        ctx.filter = "blur(18px) brightness(0.95)";
        ctx.drawImage(results.image, 0, 0, w, h);
        ctx.filter = "none";
      } else if (bg.type === "image" && bgImgRef.current) {
        ctx.drawImage(bgImgRef.current, 0, 0, w, h);
      } else if (bg.type === "color" && bg.value) {
        ctx.fillStyle = bg.value;
        ctx.fillRect(0, 0, w, h);
      } else {
        // "none" — original background
        ctx.drawImage(results.image, 0, 0, w, h);
      }

      // If replacing background, mask person and draw them on top
      if (bg.type !== "none") {
        ctx.globalCompositeOperation = "destination-out";
        // Everywhere the mask is 1 (person) we knock out the background layer
        ctx.drawImage(results.segmentationMask, 0, 0, w, h);
        ctx.globalCompositeOperation = "destination-over";
        // Draw the person on the erased area
        ctx.drawImage(results.image, 0, 0, w, h);
      }
      ctx.restore();
    });
    segRef.current = seg;

    const interval = 1000 / Math.max(6, Math.min(30, fps));
    const loop = async (t) => {
      if (cancelled) return;
      if (!video.videoWidth || video.paused) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      if (t - lastRunRef.current >= interval) {
        lastRunRef.current = t;
        try { await seg.send({ image: video }); } catch { /* noop */ }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { seg.close(); } catch { /* noop */ }
      segRef.current = null;
      firedReadyRef.current = false;
    };
  }, [sourceVideoRef, fps, onReady]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="live-effects-canvas"
      className={className}
      style={{ filter: filterCss || "none", transform: "scaleX(1)" }}
    />
  );
}
